import { supabase } from "@/lib/supabase/client"

/**
 * Internal-staff "acting as an organization" sessions.
 *
 * An internal_user has no organization_id of their own, so server-side my_org_id()
 * resolves their organization from a live row in public.internal_org_sessions
 * (migration 20260716174902). Everything they can reach — ~47 RLS policies and the
 * SECURITY DEFINER RPCs behind them — keys off that one value.
 *
 * This module is the client's mirror of my_org_id(). The two MUST agree: the server
 * deliberately ignores an internal_user's own users.organization_id, so the client
 * must ignore it too, or the UI will believe it is scoped to an org the database
 * will not actually grant (or vice versa).
 *
 * Types are hand-bridged: migration 20260716174902 is not applied yet, so
 * `supabase gen types` cannot see internal_org_sessions or these RPCs. Regenerate
 * database.types.ts once it lands and drop the casts (remembering to re-apply the
 * de-recursed Json type, or Immer hits TS2589).
 */

export interface ActingOrgSession {
  organizationId: string
  organizationName: string
  startedAt: string
  expiresAt: string
}

/**
 * Cross-tab signal for acting-session changes.
 *
 * The acting org lives in the database, not the browser, so two tabs share ONE
 * session. Switch or exit in tab A and tab B is instantly wrong: its banner still
 * names the old customer and its cached loader data still shows that customer's
 * cases, while the database now grants a different org entirely — so B's next write
 * lands somewhere its own UI never mentioned. A full reload fixes the tab you
 * clicked in and does nothing for the others.
 *
 * localStorage writes fire `storage` in every OTHER tab of the origin, which is
 * exactly the fan-out needed. The value is only a nonce; no session state is kept
 * client-side (the server remains the authority).
 */
const ACTING_SESSION_CHANGED_KEY = "focus.acting-org.changed"

/** Tell other tabs the acting session changed. Safe to call when storage is unavailable. */
export function broadcastActingSessionChange(): void {
  try {
    window.localStorage.setItem(ACTING_SESSION_CHANGED_KEY, String(Date.now()))
  } catch {
    // Private mode / storage disabled. The acting tab still reloads itself; other
    // tabs fall back to their next navigation or the banner's expiry check.
  }
}

/**
 * Reload when another tab changes the acting session. Returns an unsubscribe fn.
 * Reload rather than refetch: cached loader data for the previous org must not
 * survive, and a hard navigation is the only way to be sure none of it does.
 */
export function onActingSessionChanged(handler: () => void): () => void {
  const listener = (e: StorageEvent) => {
    if (e.key === ACTING_SESSION_CHANGED_KEY) handler()
  }
  window.addEventListener("storage", listener)
  return () => window.removeEventListener("storage", listener)
}

interface StartSessionResult {
  success: boolean
  message?: string
  session?: ActingOrgSession
}

/** Shape returned by public.current_internal_org_session() / start_internal_org_session(). */
type ActingSessionRow = {
  organization_id?: string | null
  organization_name?: string | null
  started_at?: string | null
  expires_at?: string | null
  success?: boolean
  message?: string | null
} | null

const toSession = (row: ActingSessionRow): ActingOrgSession | null =>
  row?.organization_id && row.organization_name && row.expires_at
    ? {
        organizationId: row.organization_id,
        organizationName: row.organization_name,
        startedAt: row.started_at ?? "",
        expiresAt: row.expires_at,
      }
    : null

/**
 * The caller's live acting session, or null. Returns null for every non-internal
 * user — the RPC filters on auth.uid() and role, so this is safe (if wasteful) to
 * call for anyone; callers gate on role to avoid the round trip.
 *
 * Expiry is enforced server-side inside the RPC, so a stale clock on the client
 * cannot extend a session.
 */
export async function fetchActingOrgSession(): Promise<ActingOrgSession | null> {
  const { data, error } = await supabase.rpc(
    "current_internal_org_session" as never,
  )

  if (error) return null

  return toSession(data as ActingSessionRow)
}

/** Begin acting as an organization. Server enforces internal_user + admin access_level. */
export async function startActingOrgSession(
  organizationId: string,
): Promise<StartSessionResult> {
  const { data, error } = await supabase.rpc(
    "start_internal_org_session" as never,
    { p_organization_id: organizationId } as never,
  )

  if (error) return { success: false, message: error.message }

  const row = data as ActingSessionRow

  return row?.success
    ? { success: true, session: toSession(row) ?? undefined }
    : { success: false, message: row?.message ?? "Could not start session" }
}

/** End the current acting session. Idempotent. */
export async function endActingOrgSession(): Promise<void> {
  await supabase.rpc("end_internal_org_session" as never)
}

/** Organizations an internal user may act as. RLS already scopes this to internal users. */
export interface SelectableOrganization {
  id: string
  name: string
  caseCount: number
  activeCaseCount: number
  lastActivityAt: string | null
  /** The firm's Settings switch. Off means start_internal_org_session will refuse. */
  supportAccessEnabled: boolean
}

export async function fetchSelectableOrganizations(): Promise<
  SelectableOrganization[]
> {
  const [{ data: orgs }, { data: cases }] = await Promise.all([
    supabase.from("organizations").select("id, name, support_access_enabled"),
    // Explicit limit rather than relying on PostgREST's default page size (commonly
    // 1000): the counts below are computed client-side, so a silent truncation would
    // show wrong case counts and stale "last activity" with no error. 47 cases exist
    // today, so this is headroom, not a fix — past this the aggregation belongs in an
    // RPC. Cosmetic either way: the picker is chosen by name, and every org still
    // lists.
    supabase.from("cases").select("organization_id, status, updated_at").limit(5000),
  ])

  if (!orgs) return []

  // cases is org-visible to internal users via the existing `OR is_internal_user()`
  // read branch, so this aggregates client-side rather than adding an RPC.
  const byOrg = new Map<string, { total: number; active: number; last: string | null }>()

  for (const c of cases ?? []) {
    if (!c.organization_id) continue
    const entry = byOrg.get(c.organization_id) ?? { total: 0, active: 0, last: null }
    entry.total += 1
    if (c.status === "active") entry.active += 1
    if (c.updated_at && (!entry.last || c.updated_at > entry.last)) {
      entry.last = c.updated_at
    }
    byOrg.set(c.organization_id, entry)
  }

  return orgs
    .map((o) => {
      const stats = byOrg.get(o.id)
      return {
        id: o.id,
        name: o.name,
        caseCount: stats?.total ?? 0,
        activeCaseCount: stats?.active ?? 0,
        lastActivityAt: stats?.last ?? null,
        supportAccessEnabled: o.support_access_enabled,
      }
    })
    .sort(
      (a, b) =>
        b.activeCaseCount - a.activeCaseCount ||
        (b.lastActivityAt ?? "").localeCompare(a.lastActivityAt ?? "") ||
        a.name.localeCompare(b.name),
    )
}
